import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

// Colour by basis. Researcher-verified → green, mixed/researched-partially
// → amber, everything estimated / population-scaled / peer-modelled → gray.
const BASIS_STYLES = {
  Researched: { dot: "bg-emerald-500", text: "text-emerald-800", bg: "bg-emerald-50", border: "border-emerald-200" },
  Mixed:      { dot: "bg-amber-500",   text: "text-amber-800",   bg: "bg-amber-50",   border: "border-amber-200" },
  Estimated:  { dot: "bg-stone-400",   text: "text-stone-700",   bg: "bg-stone-50",   border: "border-stone-200" },
  Modelled:   { dot: "bg-stone-400",   text: "text-stone-700",   bg: "bg-stone-50",   border: "border-stone-200" },
};

const COMPONENT_LABELS = {
  publishing:               "Publishing",
  digital_content_creation: "Digital content",
  creative_agencies:        "Creative agencies",
  media_gaming_leisure:     "Media, gaming & leisure",
};

// Compact, tooltip-only chip that flags the provenance basis of the
// Creative & Media score. `provenance` is the object returned by the
// backend under research_scores.creative_provenance (or per hub-card).
// Renders nothing when no data is available (fail-safe).
export function CreativeProvenanceChip({ provenance, testId = "creative-provenance-chip" }) {
  if (!provenance || !provenance.basis) return null;
  const basis = provenance.basis;
  const style = BASIS_STYLES[basis] || BASIS_STYLES.Modelled;
  const confidence = provenance.confidence || "—";
  const components = provenance.components || {};
  const r = provenance.researched_components;
  const e = provenance.estimated_components;
  const m = provenance.peer_modelled_components;

  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            data-testid={testId}
            className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full border cursor-help ${style.bg} ${style.text} ${style.border}`}
          >
            <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
            {basis}
          </span>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[280px] bg-white text-stone-800 border border-stone-200 shadow-lg p-3 rounded-md"
        >
          <div className="text-[11px]">
            <div className="flex items-center justify-between font-semibold text-stone-900 mb-1">
              <span>Creative & Media provenance</span>
              <span className={`text-[10px] ${style.text}`}>{basis}</span>
            </div>
            <div className="text-stone-600 mb-2">
              Confidence: <span className="font-medium text-stone-800">{confidence}</span>
              {r != null && e != null && (
                <> · {r}/4 researched · {e}/4 estimated{m && m !== "0" ? ` · ${m}/4 modelled` : ""}</>
              )}
            </div>
            {Object.keys(components).length > 0 && (
              <div className="border-t border-stone-100 pt-2 space-y-0.5">
                {Object.entries(components).map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-3">
                    <span className="text-stone-500">{COMPONENT_LABELS[k] || k}</span>
                    <span className="text-stone-700 font-medium">{v || "—"}</span>
                  </div>
                ))}
              </div>
            )}
            {provenance.evidence_geography && (
              <div className="mt-2 text-[10px] text-stone-500 italic">
                {provenance.evidence_geography}
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default CreativeProvenanceChip;
